import { SignInIdentifier } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { getStudentIdentifierChannels, type StudentChannel } from '@/apis/experience/student-login';
import { SmartInputField, StudentIdentifierField } from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { useStudentLogin } from '@/hooks/use-student-login';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import type { VerificationCodeIdentifier } from '@/types';
import { UserFlow } from '@/types';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

import ChannelPicker from '../ChannelPicker';

import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly defaultValue?: string;
  readonly enabledTypes: VerificationCodeIdentifier[];
};

type FormState = {
  identifier: {
    type?: VerificationCodeIdentifier;
    value: string;
  };
};

const ForgotPasswordForm = ({ className, autoFocus, defaultValue = '', enabledTypes }: Props) => {
  const { t } = useTranslation();
  const { errorMessage, clearErrorMessage, onSubmit } = useSendVerificationCode(
    UserFlow.ForgotPassword
  );

  const { setForgotPasswordIdentifierInputValue } = useContext(UserInteractionContext);
  const { isStudentLoginEnabled } = useStudentLogin();

  // Student-mode local state: the entered student/registration number, the
  // resolved masked channels, and a generic (anti-enumeration) lookup error.
  const [studentIdentifier, setStudentIdentifier] = useState(defaultValue);
  const [channels, setChannels] = useState<StudentChannel[]>([]);
  const [studentError, setStudentError] = useState<string>();
  const [isLookingUp, setIsLookingUp] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: {
      identifier: {
        value: defaultValue,
      },
    },
  });

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage();

      await handleSubmit(async ({ identifier: { type, value } }) => {
        if (!type) {
          return;
        }

        // Cache or update the forgot password identifier input value
        setForgotPasswordIdentifierInputValue({ type, value });

        await onSubmit({ identifier: type, value });
      })(event);
    },
    [clearErrorMessage, handleSubmit, onSubmit, setForgotPasswordIdentifierInputValue]
  );

  const onStudentChange = useCallback((data: IdentifierInputValue) => {
    setStudentIdentifier(data.value);
    setStudentError(undefined);
  }, []);

  // Resolve the student/registration number to the user's masked contact
  // channels. A miss yields a generic error and never reveals account existence.
  const onStudentLookup = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      clearErrorMessage();
      setStudentError(undefined);

      if (!studentIdentifier.trim()) {
        setStudentError(getGeneralIdentifierErrorMessage([SignInIdentifier.Username], 'required'));
        return;
      }

      setIsLookingUp(true);
      try {
        const { accountExists, channels: found } =
          await getStudentIdentifierChannels(studentIdentifier);
        if (found.length === 0) {
          // A known account with no email/phone on file gets a specific, actionable
          // message; an unknown identifier gets the generic invalid message so it
          // cannot be distinguished from a wrong number.
          setStudentError(
            accountExists
              ? String(t('error.no_recovery_contact'))
              : getGeneralIdentifierErrorMessage([SignInIdentifier.Username], 'invalid')
          );
          return;
        }
        setChannels(found);
      } finally {
        setIsLookingUp(false);
      }
    },
    [clearErrorMessage, studentIdentifier, t]
  );

  // Send the reset code to the channel the student picked.
  const onPickChannel = useCallback(
    async (channel: StudentChannel) => {
      const type = channel.type === 'email' ? SignInIdentifier.Email : SignInIdentifier.Phone;
      setForgotPasswordIdentifierInputValue({ type, value: channel.value });
      await onSubmit({ identifier: type, value: channel.value });
    },
    [onSubmit, setForgotPasswordIdentifierInputValue]
  );

  if (isStudentLoginEnabled) {
    return (
      <form className={classNames(styles.form, className)} onSubmit={onStudentLookup}>
        {channels.length === 0 ? (
          <>
            <StudentIdentifierField
              autoFocus={autoFocus}
              className={styles.inputField}
              defaultValue={defaultValue}
              isDanger={!!studentError}
              errorMessage={studentError}
              onChange={onStudentChange}
            />
            {errorMessage && (
              <ErrorMessage className={styles.formErrors}>{errorMessage}</ErrorMessage>
            )}
            <CaptchaBox />
            <Button title="action.continue" htmlType="submit" isLoading={isLookingUp} />
            <input hidden type="submit" />
          </>
        ) : (
          <ChannelPicker channels={channels} isSubmitting={isSubmitting} onPick={onPickChannel} />
        )}
      </form>
    );
  }

  return (
    <form className={classNames(styles.form, className)} onSubmit={onSubmitHandler}>
      <Controller
        control={control}
        name="identifier"
        rules={{
          validate: (identifier) => {
            const { type, value } = identifier;

            if (!type || !value) {
              return getGeneralIdentifierErrorMessage(enabledTypes, 'required');
            }

            const errorMessage = validateIdentifierField(type, value);

            if (errorMessage) {
              return typeof errorMessage === 'string'
                ? t(`error.${errorMessage}`)
                : t(`error.${errorMessage.code}`, errorMessage.data ?? {});
            }

            return true;
          },
        }}
        render={({ field, formState: { defaultValues } }) => (
          <SmartInputField
            autoFocus={autoFocus}
            className={styles.inputField}
            {...field}
            defaultValue={defaultValues?.identifier?.value}
            isDanger={!!errors.identifier}
            errorMessage={errors.identifier?.message}
            enabledTypes={enabledTypes}
          />
        )}
      />

      {errorMessage && <ErrorMessage className={styles.formErrors}>{errorMessage}</ErrorMessage>}

      <CaptchaBox />
      <Button title="action.continue" htmlType="submit" isLoading={isSubmitting} />

      <input hidden type="submit" />
    </form>
  );
};

export default ForgotPasswordForm;
